import React, { useState, useEffect } from 'react';
import { portalQuotesAPI } from '../../services/portalAPI';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface QuoteItem {
  id: number;
  formula: number;
  formula_name: string;
  is_pelleted: boolean;
  total_quantity_lbs: number;
  number_of_packages: number;
  package_size_lbs: number;
  unit_price_per_lb: string;
  line_total: string;
}

interface Quote {
  id: number;
  quote_number: string;
  quote_date: string;
  expiration_date: string;
  days_until_expiration: number;
  status: string;
  status_display: string;
  shipping_method: string;
  is_rush: boolean;
  is_tax_exempt: boolean;
  total_weight_lbs: number;
  estimated_pallets: number;
  subtotal: string;
  tax_amount: string;
  total_amount: string;
  special_instructions: string;
  notes: string;
  converted_to_order: number | null;
  items: QuoteItem[];
}

const PortalQuotes: React.FC = () => {
  const { user } = usePortalAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [customerPONumber, setCustomerPONumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const response = await portalQuotesAPI.getAll();
      // Handle both paginated response {results: [...]} and direct array [...]
      setQuotes(response.data.results || response.data || []);
    } catch (err: any) {
      setError('Failed to load quotes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewQuoteDetails = async (quoteId: number) => {
    try {
      const response = await portalQuotesAPI.getById(quoteId);
      setSelectedQuote(response.data);
    } catch (err: any) {
      console.error('Failed to load quote details:', err);
    }
  };

  const closeDetails = () => {
    setSelectedQuote(null);
    setShowAcceptModal(false);
    setDeliveryDate('');
    setCustomerPONumber('');
  };

  const handleAcceptClick = () => {
    if (selectedQuote) {
      setShowAcceptModal(true);
    }
  };

  const handleAcceptQuote = async () => {
    if (!selectedQuote || !deliveryDate) return;

    try {
      setSubmitting(true);
      await portalQuotesAPI.accept(selectedQuote.id, {
        delivery_date: deliveryDate,
        shipping_method: selectedQuote.shipping_method,
        customer_po_number: customerPONumber,
      });

      alert('Quote accepted successfully! Your order has been created.');
      closeDetails();
      loadQuotes();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to accept quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineQuote = async () => {
    if (!selectedQuote) return;

    if (!window.confirm('Are you sure you want to decline this quote?')) {
      return;
    }

    try {
      setSubmitting(true);
      await portalQuotesAPI.decline(selectedQuote.id);
      alert('Quote declined');
      closeDetails();
      loadQuotes();
    } catch (err: any) {
      alert('Failed to decline quote');
    } finally {
      setSubmitting(false);
    }
  };

  const canAcceptQuote = (quote: Quote): boolean => {
    return (
      quote.status !== 'CANCELLED' &&
      quote.status !== 'ACCEPTED' &&
      !quote.converted_to_order &&
      quote.days_until_expiration >= 0 &&
      user?.permissions.can_place_orders === true
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading quotes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="quotes" />

      <div style={styles.header}>
        <h1 style={styles.title}>Quotes</h1>
        <TutorialHelpButton pageKey="quotes" />
      </div>

      {!quotes || !Array.isArray(quotes) || quotes.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No quotes found</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="portal-table-desktop" style={styles.table} data-tutorial="quotes-table">
            <table style={styles.tableElement}>
              <thead>
                <tr>
                  <th style={styles.th}>Quote #</th>
                  <th style={styles.th}>Quote Date</th>
                  <th style={styles.th}>Expiration</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Total Amount</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote, index) => (
                  <tr key={quote.id}>
                    <td style={styles.td}>{quote.quote_number}</td>
                    <td style={styles.td}>{new Date(quote.quote_date).toLocaleDateString()}</td>
                    <td style={styles.td} data-tutorial={index === 0 ? "expiration-column" : undefined}>
                      {quote.days_until_expiration < 0 ? (
                        <span style={styles.expired}>Expired</span>
                      ) : (
                        <span>
                          {new Date(quote.expiration_date).toLocaleDateString()}
                          <br />
                          <span style={styles.daysRemaining}>({quote.days_until_expiration} days)</span>
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={getStatusStyle(quote.status)}>{quote.status_display}</span>
                    </td>
                    <td style={styles.td}>${parseFloat(quote.total_amount).toFixed(2)}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => viewQuoteDetails(quote.id)}
                        style={styles.viewButton}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="portal-cards-mobile">
            {quotes.map((quote) => (
              <div key={quote.id} className="portal-mobile-card">
                <div className="portal-mobile-card-header">
                  <span className="portal-mobile-card-title">{quote.quote_number}</span>
                  <span style={getStatusStyle(quote.status)}>{quote.status_display}</span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Quote Date</span>
                  <span className="portal-mobile-card-value">{new Date(quote.quote_date).toLocaleDateString()}</span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Expiration</span>
                  <span className="portal-mobile-card-value">
                    {quote.days_until_expiration < 0 ? (
                      <span style={styles.expired}>Expired</span>
                    ) : (
                      <span>{new Date(quote.expiration_date).toLocaleDateString()} ({quote.days_until_expiration} days)</span>
                    )}
                  </span>
                </div>
                <div className="portal-mobile-card-row">
                  <span className="portal-mobile-card-label">Total Amount</span>
                  <span className="portal-mobile-card-value" style={{ fontWeight: 'bold', color: '#E73C3E' }}>${parseFloat(quote.total_amount).toFixed(2)}</span>
                </div>
                <div className="portal-mobile-card-actions">
                  <button onClick={() => viewQuoteDetails(quote.id)} style={styles.viewButton}>
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Quote Details Modal */}
      {selectedQuote && !showAcceptModal && (
        <div style={styles.modal} onClick={closeDetails}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Quote Details - {selectedQuote.quote_number}</h2>
              <button onClick={closeDetails} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.detailsGrid}>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Quote Date</div>
                  <div style={styles.detailValue}>{new Date(selectedQuote.quote_date).toLocaleDateString()}</div>
                </div>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Expiration Date</div>
                  <div style={styles.detailValue}>
                    {new Date(selectedQuote.expiration_date).toLocaleDateString()}
                    {selectedQuote.days_until_expiration < 0 && (
                      <span style={styles.expired}> (Expired)</span>
                    )}
                  </div>
                </div>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Status</div>
                  <div style={styles.detailValue}>
                    <span style={getStatusStyle(selectedQuote.status)}>{selectedQuote.status_display}</span>
                  </div>
                </div>
                <div style={styles.detailGroup}>
                  <div style={styles.detailLabel}>Shipping Method</div>
                  <div style={styles.detailValue}>{selectedQuote.shipping_method}</div>
                </div>
                {selectedQuote.is_rush && (
                  <div style={styles.detailGroup}>
                    <div style={styles.detailLabel}>Rush Order</div>
                    <div style={styles.detailValue}>Yes</div>
                  </div>
                )}
              </div>

              {selectedQuote.special_instructions && (
                <div style={styles.instructionsBox}>
                  <div style={styles.detailLabel}>Special Instructions</div>
                  <div style={styles.detailValue}>{selectedQuote.special_instructions}</div>
                </div>
              )}

              {selectedQuote.notes && (
                <div style={styles.instructionsBox}>
                  <div style={styles.detailLabel}>Notes</div>
                  <div style={styles.detailValue}>{selectedQuote.notes}</div>
                </div>
              )}

              <h3 style={styles.itemsTitle}>Quote Items</h3>
              <table style={styles.itemsTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>Formula</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Quantity</th>
                    <th style={styles.th}>Packages</th>
                    <th style={styles.th}>Unit Price</th>
                    <th style={styles.th}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedQuote.items.map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}>{item.formula_name}</td>
                      <td style={styles.td}>{item.is_pelleted ? 'Pellet' : 'Mash'}</td>
                      <td style={styles.td}>{item.total_quantity_lbs.toLocaleString()} lbs</td>
                      <td style={styles.td}>{item.number_of_packages} × {item.package_size_lbs} lbs</td>
                      <td style={styles.td}>${parseFloat(item.unit_price_per_lb).toFixed(4)}/lb</td>
                      <td style={styles.td}>${parseFloat(item.line_total).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={styles.totalsBox}>
                <div style={styles.totalRow}>
                  <span>Subtotal:</span>
                  <span>${parseFloat(selectedQuote.subtotal).toFixed(2)}</span>
                </div>
                {!selectedQuote.is_tax_exempt && (
                  <div style={styles.totalRow}>
                    <span>Tax:</span>
                    <span>${parseFloat(selectedQuote.tax_amount).toFixed(2)}</span>
                  </div>
                )}
                <div style={{...styles.totalRow, ...styles.grandTotal}}>
                  <span>Total:</span>
                  <span>${parseFloat(selectedQuote.total_amount).toFixed(2)}</span>
                </div>
                <div style={styles.totalRow}>
                  <span>Total Weight:</span>
                  <span>{selectedQuote.total_weight_lbs.toLocaleString()} lbs</span>
                </div>
                <div style={styles.totalRow}>
                  <span>Estimated Pallets:</span>
                  <span>{selectedQuote.estimated_pallets}</span>
                </div>
              </div>

              {canAcceptQuote(selectedQuote) && (
                <div style={styles.actionButtons}>
                  <button onClick={handleAcceptClick} style={styles.acceptButton} data-tutorial="accept-btn">
                    Accept Quote
                  </button>
                  <button onClick={handleDeclineQuote} style={styles.declineButton} disabled={submitting}>
                    Decline Quote
                  </button>
                </div>
              )}

              {selectedQuote.converted_to_order && (
                <div style={styles.convertedMessage}>
                  This quote has been converted to an order
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Accept Quote Modal */}
      {showAcceptModal && selectedQuote && (
        <div style={styles.modal} onClick={() => setShowAcceptModal(false)}>
          <div style={{...styles.modalContent, maxWidth: '500px'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Accept Quote</h2>
              <button onClick={() => setShowAcceptModal(false)} style={styles.closeButton}>×</button>
            </div>

            <div style={styles.modalBody}>
              <p style={styles.acceptMessage}>
                You are about to accept quote <strong>{selectedQuote.quote_number}</strong> and convert it to an order.
              </p>

              <div style={styles.formGroup}>
                <label htmlFor="deliveryDate" style={styles.label}>
                  Delivery Date <span style={styles.required}>*</span>
                </label>
                <input
                  id="deliveryDate"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  required
                  style={styles.input}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="customerPO" style={styles.label}>
                  Your PO Number (Optional)
                </label>
                <input
                  id="customerPO"
                  type="text"
                  value={customerPONumber}
                  onChange={(e) => setCustomerPONumber(e.target.value)}
                  placeholder="Enter your PO number"
                  style={styles.input}
                />
              </div>

              <div style={styles.actionButtons}>
                <button
                  onClick={handleAcceptQuote}
                  disabled={!deliveryDate || submitting}
                  style={{
                    ...styles.acceptButton,
                    ...((!deliveryDate || submitting) ? styles.buttonDisabled : {}),
                  }}
                >
                  {submitting ? 'Processing...' : 'Confirm & Create Order'}
                </button>
                <button
                  onClick={() => setShowAcceptModal(false)}
                  style={styles.cancelButton}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PortalFooter />
    </div>
  );
};

const getStatusStyle = (status: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '0.35rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'inline-block',
  };

  switch (status) {
    case 'DRAFT':
      return { ...baseStyle, backgroundColor: 'rgba(195, 173, 139, 0.2)', color: '#3F2021', border: '1px solid #C3AD8B' };
    case 'SENT':
      return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
    case 'ACCEPTED':
      return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
    case 'CANCELLED':
      return { ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b' };
    default:
      return { ...baseStyle, backgroundColor: 'rgba(195, 173, 139, 0.2)', color: '#3F2021', border: '1px solid #C3AD8B' };
  }
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: 0,
  },
  table: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    overflowX: 'auto',
  },
  tableElement: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    borderBottom: '2px solid #C3AD8B',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
    textTransform: 'uppercase',
    backgroundColor: '#FAF9F6',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #C3AD8B',
    fontSize: '0.95rem',
    color: '#3F2021',
  },
  viewButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  expired: {
    color: '#E73C3E',
    fontWeight: '500',
  },
  daysRemaining: {
    fontSize: '0.8rem',
    color: '#508590',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    padding: '3rem',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '1.125rem',
    color: '#508590',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.125rem',
    color: '#508590',
  },
  error: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(63, 32, 33, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '2px solid #C3AD8B',
    backgroundColor: '#3F2021',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#FAF9F6',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#FAF9F6',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
    backgroundColor: '#FAF9F6',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  },
  detailGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#508590',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: '1rem',
    color: '#3F2021',
  },
  instructionsBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #C3AD8B',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  itemsTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#3F2021',
    marginBottom: '1rem',
  },
  itemsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1.5rem',
    backgroundColor: '#FFFFFF',
    border: '1px solid #C3AD8B',
    borderRadius: '4px',
  },
  totalsBox: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #C3AD8B',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.95rem',
    color: '#3F2021',
  },
  grandTotal: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    borderTop: '2px solid #E73C3E',
    paddingTop: '0.75rem',
    marginTop: '0.5rem',
    color: '#E73C3E',
  },
  actionButtons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  acceptButton: {
    flex: 1,
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(108, 128, 98, 0.3)',
  },
  declineButton: {
    flex: 1,
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#508590',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  convertedMessage: {
    backgroundColor: 'rgba(108, 128, 98, 0.2)',
    color: '#6C8062',
    border: '1px solid #6C8062',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
    fontWeight: '500',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '0.5rem',
  },
  required: {
    color: '#E73C3E',
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '16px', // Prevents iOS auto-zoom
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    minHeight: '44px', // Touch target
  },
  acceptMessage: {
    fontSize: '1rem',
    color: '#3F2021',
    marginBottom: '1.5rem',
  },
};

export default PortalQuotes;
